import { useState, type ReactNode, type KeyboardEvent } from 'react';
import type { TesterQueueJob } from '@runhuman/shared';
import { priorityScoreToBand } from '@runhuman/shared';
import { getJobTitle } from './job-title';

interface Props {
  /**
   * The job displayed in this row. Accepts the base shape
   * (`TesterQueueJob`) — both in-progress and late-submission jobs extend
   * it, and the row reads the same fields off all three.
   */
  readonly job: TesterQueueJob;
  /**
   * Variant marker passed down to the `<li>` className so per-section
   * styling (e.g. the in-progress border) keeps working without a separate
   * `<JobRow>` per section.
   */
  readonly variant?: 'in-progress';
  /**
   * Additional badge content rendered next to priority/device/time —
   * sections use this for stuff that isn't on the base job (in-progress
   * tester alias, etc.).
   */
  readonly badgesExtra?: ReactNode;
  /**
   * Trailing slot for the per-section action affordance (Claim button,
   * "Resume to submit" button, tester alias text). Right-aligned against
   * the chevron.
   */
  readonly trailing?: ReactNode;
  /**
   * Compact "X ago" string for the time badge. Sections compute their
   * own (`createdAt` for available, `claimedAt` for in-progress, …) so
   * the row stays unaware of which timestamp it's showing.
   */
  readonly timeBadge: string;
}

/**
 * Single queue row, shared across Available / In-Progress / Late-Submission
 * sections (#3924 + #3925).
 *
 * Layout:
 *   • Top line: badges (priority / device / time) on the left, trailing slot
 *     + chevron on the right.
 *   • Title line: `Organization / Project` (or URL fallback) — the headline
 *     a tester sees first when scanning the queue.
 *   • Secondary line: URL — kept visible but de-emphasised so the row still
 *     answers "what site am I about to test?" at a glance.
 *   • Expanded fold (rendered only when the chevron is toggled): the job's
 *     description, plus any additional supporting metadata that's already
 *     on the wire.
 *
 * The chevron + body fold mirror the web `JobCard`'s `useExpandableCard`
 * pattern from #3384 — same affordance, denser layout for Discord's narrow
 * iframe. State is per-row; the rest of the queue's expansion state isn't
 * disturbed when one row toggles.
 *
 * Click target: the entire title row toggles expansion. Trailing-slot
 * content (Claim, Resume) gets `e.stopPropagation()` from its own onClick
 * via the `<button>` semantics — clicking a Claim button doesn't ALSO
 * expand the row. Keyboard accessible: `Enter` / `Space` on the row toggles.
 */
export function JobRow({ job, variant, badgesExtra, trailing, timeBadge }: Props) {
  const [expanded, setExpanded] = useState(false);
  const title = getJobTitle(job);

  const variantClass = variant === 'in-progress' ? ' queue__row--in-progress' : '';
  const expandedClass = expanded ? ' queue__row--expanded' : '';
  const className = `queue__row${variantClass}${expandedClass}`;

  const toggle = () => setExpanded((prev) => !prev);
  const handleClick = () => {
    // Skip toggle when the user just finished highlighting text inside the
    // row — otherwise a click-drag to select the description collapses the
    // row on mouseup, discarding the selection before it can be copied.
    if (window.getSelection()!.toString().length > 0) return;
    toggle();
  };
  const handleKey = (event: KeyboardEvent<HTMLLIElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggle();
    }
  };

  return (
    <li
      className={className}
      data-job-id={job.id}
      onClick={handleClick}
      onKeyDown={handleKey}
      role="button"
      tabIndex={0}
      aria-expanded={expanded}
    >
      <div className="queue__row-top">
        <div className="queue__meta">
          <span className="queue__priority">{priorityScoreToBand(job.priorityScore)}</span>
          {job.deviceClass && <span className="queue__device">{job.deviceClass}</span>}
          <span className="queue__time">{timeBadge}</span>
          {badgesExtra}
        </div>
        <div className="queue__trailing">
          {trailing}
          <span
            className={`queue__chevron${expanded ? ' queue__chevron--expanded' : ''}`}
            aria-hidden="true"
          >
            ▾
          </span>
        </div>
      </div>
      <div className="queue__title">{title.primary}</div>
      {title.secondary && <div className="queue__url">{title.secondary}</div>}
      {expanded && <JobDetailsFold job={job} />}
    </li>
  );
}

/**
 * Expanded body shown when the row is opened (#3925). Surfaces every
 * tester-relevant field already on the wire so the tester can decide whether
 * to claim without leaving Discord. Empty descriptions get a short
 * placeholder line so the chevron isn't lying when there's nothing else.
 */
function JobDetailsFold({ job }: { readonly job: TesterQueueJob }) {
  const description = job.description.trim();
  return (
    <div className="queue__details">
      <p className="queue__description">
        {description.length > 0 ? description : 'No description provided.'}
      </p>
    </div>
  );
}
